/**
 * LocationTransition
 *
 * Wrapper component that applies CSS transitions when entering/exiting locations.
 * Supports fade, slide, zoom, and no-animation modes.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { TransitionType, TransitionDirection } from '../../../types/spatial';

export interface LocationTransitionProps {
  children: React.ReactNode;
  /** The type of transition animation */
  type: TransitionType;
  /** Direction for slide transitions */
  direction?: TransitionDirection;
  /** Duration in milliseconds */
  duration: number;
  /** Whether the component is entering (true) or exiting (false) */
  isEntering: boolean;
  /** Callback when transition completes */
  onTransitionEnd?: () => void;
  /** CSS class name for additional styling */
  className?: string;
}

/**
 * Get CSS classes for the transition based on type and state
 */
function getTransitionClasses(
  type: TransitionType,
  direction: TransitionDirection | undefined,
  isEntering: boolean,
  isActive: boolean
): string {
  const baseClasses = 'transition-all';

  if (type === 'none') {
    return baseClasses;
  }

  if (type === 'fade') {
    if (!isActive) {
      return `${baseClasses} opacity-0`;
    }
    return `${baseClasses} opacity-100`;
  }

  if (type === 'zoom') {
    if (!isActive) {
      return `${baseClasses} opacity-0 scale-95`;
    }
    return `${baseClasses} opacity-100 scale-100`;
  }

  if (type === 'slide') {
    const dir = direction || 'left';
    if (!isActive) {
      // Starting position based on direction
      switch (dir) {
        case 'left':
          return `${baseClasses} opacity-0 translate-x-full`;
        case 'right':
          return `${baseClasses} opacity-0 -translate-x-full`;
        case 'up':
          return `${baseClasses} opacity-0 translate-y-full`;
        case 'down':
          return `${baseClasses} opacity-0 -translate-y-full`;
      }
    }
    return `${baseClasses} opacity-100 translate-x-0 translate-y-0`;
  }

  return baseClasses;
}

export const LocationTransition: React.FC<LocationTransitionProps> = ({
  children,
  type,
  direction,
  duration,
  isEntering,
  onTransitionEnd,
  className = '',
}) => {
  const [isActive, setIsActive] = useState(!isEntering);

  useEffect(() => {
    if (isEntering) {
      // Trigger enter animation after mount
      const timer = requestAnimationFrame(() => {
        setIsActive(true);
      });
      return () => cancelAnimationFrame(timer);
    } else {
      // Trigger exit animation
      setIsActive(false);
    }
  }, [isEntering]);

  const handleTransitionEnd = useCallback(() => {
    if (onTransitionEnd) {
      onTransitionEnd();
    }
  }, [onTransitionEnd]);

  const transitionClasses = getTransitionClasses(type, direction, isEntering, isActive);
  const durationStyle = type !== 'none' ? { transitionDuration: `${duration}ms` } : {};

  return (
    <div
      className={`${transitionClasses} ${className}`}
      style={durationStyle}
      onTransitionEnd={handleTransitionEnd}
      data-testid="location-transition"
      data-transition-type={type}
      data-transition-active={isActive}
    >
      {children}
    </div>
  );
};

export default LocationTransition;
